/**
 * 学生信息卡片组件
 * 显示学生基本信息和信息完整度可视化
 * 支持按学段（高中/初中）显示不同字段和配色
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, MapPin, GraduationCap, Award, ChevronDown, ChevronUp, Edit, AlertCircle } from "lucide-react";
import { useState } from "react";
import type { StudentInfo } from "@/api/chat";
export type { StudentInfo };

interface StudentInfoCardProps {
  studentInfo: StudentInfo;
  completionRate: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  onEdit?: () => void;
  educationLevel?: 'senior' | 'junior';
}

export function StudentInfoCard({
  studentInfo,
  completionRate,
  isExpanded = true,
  onToggle,
  onEdit,
  educationLevel = 'senior',
}: StudentInfoCardProps) {
  const [isExpandedState, setIsExpandedState] = useState(isExpanded);

  const isJunior = educationLevel === 'junior';

  const handleToggle = () => {
    setIsExpandedState(!isExpandedState);
    onToggle?.();
  };

  const getCompletionColor = () => {
    if (completionRate >= 80) return "text-green-600";
    if (completionRate >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getStreamText = () => {
    if (isJunior) return "无选科";
    if (studentInfo.stream === "physics") return "物理类";
    if (studentInfo.stream === "history") return "历史类";
    return "未选择";
  };

  // 学段主题色
  const themeColors = isJunior
    ? {
        bg: "bg-blue-50",
        border: "border-blue-100",
        text: "text-blue-700",
        scoreText: "text-blue-700",
        icon: "text-blue-600",
      }
    : {
        bg: "bg-blue-50",
        border: "border-blue-100",
        text: "text-blue-700",
        scoreText: "text-blue-700",
        icon: "text-blue-600",
      };

  const getUniversityLevelBadge = () => {
    if (isJunior) {
      // 初中显示目标高中层次
      switch (studentInfo.targetUniversityLevel) {
        case "985":
          return <Badge variant="secondary" className="bg-purple-100 text-purple-800">重点高中</Badge>;
        case "211":
          return <Badge variant="secondary" className="bg-blue-100 text-blue-800">一级达标</Badge>;
        case "双一流":
          return <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">二级达标</Badge>;
        case "普通本科":
          return <Badge variant="secondary" className="bg-gray-100 text-gray-800">普通高中</Badge>;
        case "专科":
          return <Badge variant="secondary" className="bg-orange-100 text-orange-800">职高/中专</Badge>;
        default:
          return null;
      }
    }

    switch (studentInfo.targetUniversityLevel) {
      case "985":
        return <Badge variant="secondary" className="bg-purple-100 text-purple-800">985院校</Badge>;
      case "211":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">211院校</Badge>;
      case "双一流":
        return <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">双一流</Badge>;
      case "普通本科":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">普通本科</Badge>;
      case "专科":
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">专科</Badge>;
      default:
        return null;
    }
  };

  // 环形进度条
  const CircularProgress = ({ value }: { value: number }) => {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg
          className="transform -rotate-90"
          width={radius * 2 + 20}
          height={radius * 2 + 20}
        >
          <circle
            cx={radius + 10}
            cy={radius + 10}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={radius + 10}
            cy={radius + 10}
            r={radius}
            stroke={value >= 80 ? "#10b981" : value >= 60 ? "#f59e0b" : "#ef4444"}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={`text-xl font-bold ${getCompletionColor()}`}>
            {Math.round(value)}%
          </span>
          <span className="text-xs text-gray-500">完整度</span>
        </div>
      </div>
    );
  };

  return (
    <Card className={`w-full shadow-md border-l-4 ${isJunior ? 'border-l-blue-500' : 'border-l-red-500'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className={`w-5 h-5 ${themeColors.icon}`} />
            学生信息
          </CardTitle>
          <div className="flex items-center gap-2">
            <CircularProgress value={completionRate} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="h-8 px-2"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggle}
              className="h-8 px-2"
            >
              {isExpandedState ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
        {completionRate < 80 && (
          <div className="mt-2 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800 font-medium">
                信息完整度不足
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                {isJunior
                  ? "补充完整学生信息后，AI将为您推荐更精准的升学建议"
                  : "补充完整学生信息后，AI将为您推荐更精准的志愿填报建议"
                }
              </p>
            </div>
          </div>
        )}
      </CardHeader>

      {isExpandedState && (
        <CardContent className="space-y-4 pt-0">
          {/* 基本信息 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-gray-500">姓名</p>
              <p className="font-medium">{studentInfo.name || "未填写"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">学校</p>
              <p className="font-medium truncate">{studentInfo.school || "未填写"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">年级</p>
              <p className="font-medium">{studentInfo.grade || "未选择"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">{isJunior ? "学段" : "选科"}</p>
              <p className="font-medium">{getStreamText()}</p>
            </div>
          </div>

          {/* 成绩信息 */}
          {studentInfo.totalScore && (
            <div className={`p-3 ${themeColors.bg} border ${themeColors.border} rounded-md`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-600 flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  最近成绩
                </p>
                {getUniversityLevelBadge()}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">总分：</span>
                  <span className={`font-bold text-lg ml-1 ${themeColors.scoreText}`}>
                    {studentInfo.totalScore}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">分</span>
                </div>
                <div>
                  <span className="text-gray-500">{isJunior ? "折科目：" : "选科："}</span>
                  <span className="font-semibold">
                    {isJunior
                      ? (studentInfo.historyScore || studentInfo.physicsScore || "-")
                      : (studentInfo.stream === "physics" ? studentInfo.physicsScore || "-" : studentInfo.historyScore || "-")
                    }
                  </span>
                  <span className="text-xs text-gray-500 ml-1">分</span>
                </div>
                <div>
                  <span className="text-gray-500">语文：</span>
                  <span className="font-semibold">{studentInfo.chineseScore || "-"}</span>
                  <span className="text-xs text-gray-500 ml-1">分</span>
                </div>
                <div>
                  <span className="text-gray-500">数学：</span>
                  <span className="font-semibold">{studentInfo.mathScore || "-"}</span>
                  <span className="text-xs text-gray-500 ml-1">分</span>
                </div>
                <div>
                  <span className="text-gray-500">英语：</span>
                  <span className="font-semibold">{studentInfo.englishScore || "-"}</span>
                  <span className="text-xs text-gray-500 ml-1">分</span>
                </div>
              </div>
            </div>
          )}

          {/* 偏好信息 */}
          <div className="space-y-2">
            {Array.isArray(studentInfo.targetCities) && studentInfo.targetCities.length > 0 && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">{isJunior ? "目标区域" : "目标城市"}</p>
                  <div className="flex flex-wrap gap-1">
                    {studentInfo.targetCities.map((city, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {city}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {Array.isArray(studentInfo.preferredMajors) && studentInfo.preferredMajors.length > 0 && (
              <div className="flex items-start gap-2">
                <GraduationCap className="w-4 h-4 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">{isJunior ? "兴趣方向" : "偏好专业"}</p>
                  <div className="flex flex-wrap gap-1">
                    {studentInfo.preferredMajors.map((major, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {major}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
